import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#F6EBE2]/85 border-b border-elle"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20 md:h-24">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-baseline gap-2 group"
        >
          <span className="font-serif-display text-3xl md:text-4xl text-elle-green leading-none">
            Ell'e
          </span>
          <span className="font-body text-[10px] tracking-overline uppercase text-elle-green mt-1">
            Coffee
          </span>
        </Link>

        <ul className="hidden md:flex items-center gap-10">
          {NAV.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `link-underline font-body text-[11px] tracking-overline uppercase ${
                    isActive ? "text-elle-red" : "text-elle-green"
                  }`
                }
              >
                {item.label}
              </NavLink>
            </li>
          ))}
          <li>
            <Link
              to="/shop"
              data-testid="nav-cta-shop"
              className="font-body text-[11px] tracking-overline uppercase px-5 py-2.5 border border-elle-green text-elle-green hover:bg-elle-green hover:text-elle-cream transition-colors duration-500"
            >
              Shop Now
            </Link>
          </li>
        </ul>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-elle-green p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden bg-[#F6EBE2] border-t border-elle"
            data-testid="nav-mobile-menu"
          >
            <ul className="px-6 py-8 flex flex-col gap-5">
              {NAV.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    data-testid={`nav-mobile-link-${item.label.toLowerCase()}`}
                    className="font-serif-display text-3xl text-elle-green"
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
